export interface PageHistoryEntryInterface {
    key: string; // key is the identifier
    url: string; // the url of the intended breadcrumb
    type: string; // pageType definition, is used in BreadcrumbPrefix to determine label
    overwritten?: boolean; // allows for additional (non-existent) pageType Definitions to be manually added
    priority: number; // on multiple pageTypes in single render,  higher priority can overwrite lower prio type
}

interface PageHistoryEntryCollection {
    pageHistoryEntries: PageHistoryEntryInterface[];
}

export function addToHistory(entry: PageHistoryEntryInterface) {
    let pageHistoryEntries: PageHistoryEntryCollection = getPageHistoryEntries();
    // if entry is identical to first entry in list, prevent adding.
    if (
        pageHistoryEntries.pageHistoryEntries.length > 0 &&
        pageHistoryEntries.pageHistoryEntries[0].key === entry.key
    ) {
        return;
    }

    if (entry.type === 'redirect') {
        return;
    }

    if (!entry.priority) {
        entry.priority = 1;
    }

    pageHistoryEntries.pageHistoryEntries.unshift(entry);
    pageHistoryEntries.pageHistoryEntries = pageHistoryEntries.pageHistoryEntries.slice(0, 5);
    sessionStorage.setItem('pageHistory', JSON.stringify(pageHistoryEntries.pageHistoryEntries));
}

export function overwriteLatestPageHistory(entry: PageHistoryEntryInterface): void {
    // This method can be used when adding additional data to an existing entry in page history for when the default
    // plugin is not specific enough in its type determination.
    // It is possible for multiple calls to this method on the same page as components can be nested/stacked/etc.
    // The overwrite method only works once per page, then, an overwrite = true tag is set preventing further overwrites.
    // if multiple overwrites are present on the same page, you need to define the priority number.
    // higher priority means overwriting a lower priority number type.
    let pageHistoryEntries: PageHistoryEntryCollection = getPageHistoryEntries();

    if (pageHistoryEntries.pageHistoryEntries.length === 0) {
        // no page history found, yet.
        return;
    }

    // only if the first item in the row is the same key, we overwrite it.
    if (pageHistoryEntries.pageHistoryEntries[0].key !== entry.key) {
        return;
    }

    if (
        pageHistoryEntries.pageHistoryEntries[0].overwritten &&
        pageHistoryEntries.pageHistoryEntries[0].priority > entry.priority
    ) {
        return;
    }

    pageHistoryEntries.pageHistoryEntries[0].type = entry.type;
    pageHistoryEntries.pageHistoryEntries[0].overwritten = true;
    sessionStorage.setItem('pageHistory', JSON.stringify(pageHistoryEntries.pageHistoryEntries));
}

export function getPageHistoryEntries(): PageHistoryEntryCollection {
    let storedHistory = sessionStorage.getItem('pageHistory');
    if (!storedHistory) {
        return { pageHistoryEntries: [] };
    }

    let storageData = JSON.parse(storedHistory);

    if (storageData.length === 0) {
        return { pageHistoryEntries: [] };
    }

    let pageHistoryEntriesCollection: PageHistoryEntryCollection = { pageHistoryEntries: [] };

    storageData.forEach((pageHistoryData: { [x: string]: any }) => {
        pageHistoryEntriesCollection.pageHistoryEntries.push({
            key: pageHistoryData.key,
            url: pageHistoryData['url'],
            type: pageHistoryData['type'],
            overwritten: pageHistoryData['overwritten'],
            priority: pageHistoryData['priority'] ?? 1,
        });
    });

    return pageHistoryEntriesCollection;
}

export function getPreviousPage(): null | PageHistoryEntryInterface {
    let pageHistoryEntries: PageHistoryEntryCollection = getPageHistoryEntries();

    if (pageHistoryEntries.pageHistoryEntries.length < 1) {
        return null;
    }

    return pageHistoryEntries.pageHistoryEntries[1];
}

export function getCurrentPage(): null | PageHistoryEntryInterface {
    let pageHistoryEntries: PageHistoryEntryCollection = getPageHistoryEntries();

    if (pageHistoryEntries.pageHistoryEntries.length === 0) {
        return null;
    }

    return pageHistoryEntries.pageHistoryEntries[0];
}
